import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ansesAdminService from '../services/ansesAdminService';
import AvisosPorUsuario from '../components/anses/AvisosPorUsuario';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/AdminAnsesSearchPage.css';

const formatearFechaHora = (valor) => {
    if (!valor) return { fecha: '—', hora: '—' };
    try {
        const d = new Date(valor);
        return {
            fecha: d.toLocaleDateString('es-AR'),
            hora: d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        };
    } catch (e) {
        return { fecha: valor, hora: '' };
    }
};

const formatearFecha = (valor) => {
    if (!valor) return '—';
    try {
        return new Date(valor).toLocaleString('es-AR');
    } catch (e) {
        return valor;
    }
};

function AdminAnsesSearchPage() {
    const [config, setConfig] = useState(null);
    const [syncHabilitado, setSyncHabilitado] = useState(true);
    const [intervaloDias, setIntervaloDias] = useState(1);
    const [maxReintentosLogin, setMaxReintentosLogin] = useState(5);
    const [intervaloDiasAlta, setIntervaloDiasAlta] = useState(1);
    const [intervaloDiasBaja, setIntervaloDiasBaja] = useState(3);
    const [guardando, setGuardando] = useState(false);

    const [status, setStatus] = useState([]);
    const [cargandoStatus, setCargandoStatus] = useState(true);

    const [logs, setLogs] = useState([]);
    const [cargandoLogs, setCargandoLogs] = useState(true);
    const [filtroEstadoLog, setFiltroEstadoLog] = useState('TODOS');

    const cargarConfig = useCallback(async () => {
        try {
            const data = await ansesAdminService.obtenerConfig();
            setConfig(data);
            setSyncHabilitado(!!data.syncHabilitado);
            setIntervaloDias(data.intervaloDias || 1);
            setMaxReintentosLogin(data.maxReintentosLogin ?? 5);
            setIntervaloDiasAlta(data.intervaloDiasAlta ?? 1);
            setIntervaloDiasBaja(data.intervaloDiasBaja ?? 3);
        } catch (e) {
            toast.error('No se pudo cargar la configuración de ANSES.');
        }
    }, []);

    const cargarStatus = useCallback(async () => {
        try {
            const data = await ansesAdminService.obtenerStatus();
            setStatus(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error('No se pudo cargar el estado de conexiones de ANSES.');
        } finally {
            setCargandoStatus(false);
        }
    }, []);

    const cargarLogs = useCallback(async () => {
        try {
            const data = await ansesAdminService.obtenerLogs(50);
            setLogs(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error('No se pudieron cargar los logs de ANSES.');
        } finally {
            setCargandoLogs(false);
        }
    }, []);

    useEffect(() => {
        cargarConfig();
        cargarStatus();
        cargarLogs();
    }, [cargarConfig, cargarStatus, cargarLogs]);

    useEffect(() => {
        const statusPoll = setInterval(cargarStatus, 20000);
        const logsPoll = setInterval(cargarLogs, 30000);
        return () => {
            clearInterval(statusPoll);
            clearInterval(logsPoll);
        };
    }, [cargarStatus, cargarLogs]);

    const guardarConfig = async () => {
        setGuardando(true);
        try {
            const data = await ansesAdminService.actualizarConfig({
                syncHabilitado,
                intervaloDias: Number(intervaloDias),
                maxReintentosLogin: Number(maxReintentosLogin),
                intervaloDiasAlta: Number(intervaloDiasAlta),
                intervaloDiasBaja: Number(intervaloDiasBaja),
            });
            setConfig(data);
            toast.success('Configuración guardada correctamente.');
        } catch (e) {
            toast.error('No se pudo guardar la configuración.');
        } finally {
            setGuardando(false);
        }
    };

    const logsFiltrados = logs.filter((l) => filtroEstadoLog === 'TODOS' || l.estado === filtroEstadoLog);

    return (
        <div className="anses-admin-page area-anses">
            <PageHeader
                variante="plano"
                icono="search"
                titulo="Búsqueda de Expedientes ANSES"
                descripcion="Configuración, estado de conexiones y logs de sincronización"
            />

            <div className="anses-admin-grid">
                <div className="anses-admin-card">
                    <h6>Configuración</h6>

                    <div className="anses-switch-box form-check form-switch mb-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="anses-sync-habilitado"
                            checked={syncHabilitado}
                            onChange={(e) => setSyncHabilitado(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="anses-sync-habilitado">
                            Sincronización automática habilitada
                        </label>
                    </div>
                    <small style={{ color: '#888780', display: 'block', marginBottom: 18 }}>
                        Interruptor general. <strong>Apagado</strong>: el sistema no revisa nada por su cuenta, hay
                        que actualizar cada cliente a mano. <strong>Encendido</strong>: revisa periódicamente según
                        el nivel de prioridad de cada cliente (ver abajo).
                    </small>

                    <hr style={{ margin: '0 0 16px', borderColor: '#eee' }} />

                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: 700, marginBottom: 10 }}>
                        Número de reintentos
                    </div>
                    <label className="form-label" htmlFor="anses-max-reintentos" style={{ fontSize: 13 }}>
                        Intentos antes de pedir conexión manual
                    </label>
                    <input
                        id="anses-max-reintentos"
                        type="number"
                        min="0"
                        className="form-control"
                        value={maxReintentosLogin}
                        onChange={(e) => setMaxReintentosLogin(e.target.value)}
                    />
                    <small style={{ color: '#888780' }}>
                        Cuántas veces reintenta el sistema conectarse solo antes de avisar que hace falta que alguien
                        entre a mano. 0 = no reintentar automáticamente.
                    </small>

                    <hr style={{ margin: '18px 0 16px', borderColor: '#eee' }} />

                    <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: 700, marginBottom: 4 }}>
                        Nivel de prioridad
                    </div>
                    <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
                        Cada cuánto se revisan los clientes según la prioridad que les asignaron (elegida por
                        cliente desde Seguimiento de Expedientes).
                    </p>

                    <div className="mb-2">
                        <label className="form-label" htmlFor="anses-intervalo-alta" style={{ fontSize: 13 }}>Alta</label>
                        <div className="input-group">
                            <input
                                id="anses-intervalo-alta"
                                type="number"
                                min="1"
                                className="form-control"
                                value={intervaloDiasAlta}
                                onChange={(e) => setIntervaloDiasAlta(e.target.value)}
                            />
                            <span className="input-group-text">días</span>
                        </div>
                    </div>
                    <div className="mb-2">
                        <label className="form-label" htmlFor="anses-intervalo-media" style={{ fontSize: 13 }}>Media</label>
                        <div className="input-group">
                            <input
                                id="anses-intervalo-media"
                                type="number"
                                min="1"
                                className="form-control"
                                value={intervaloDias}
                                onChange={(e) => setIntervaloDias(e.target.value)}
                            />
                            <span className="input-group-text">días</span>
                        </div>
                    </div>
                    <div className="mb-2">
                        <label className="form-label" htmlFor="anses-intervalo-baja" style={{ fontSize: 13 }}>Baja</label>
                        <div className="input-group">
                            <input
                                id="anses-intervalo-baja"
                                type="number"
                                min="1"
                                className="form-control"
                                value={intervaloDiasBaja}
                                onChange={(e) => setIntervaloDiasBaja(e.target.value)}
                            />
                            <span className="input-group-text">días</span>
                        </div>
                    </div>
                    <small style={{ color: '#888780' }}>
                        Alta fuerza revisión más seguido; Baja la espacía más; Media revisa a este ritmo, salvo que
                        el trámite en sí todavía no lo necesite.
                    </small>

                    {config?.actualizadoEn && (
                        <p style={{ fontSize: 12, color: '#888780', margin: '16px 0 12px' }}>
                            Última actualización: {formatearFecha(config.actualizadoEn)}
                        </p>
                    )}
                    <button type="button" className="btn btn-primary btn-sm" onClick={guardarConfig} disabled={guardando}>
                        {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                </div>

                <div className="anses-admin-card">
                    <h6>Estado de conexiones por cliente</h6>
                    {cargandoStatus ? (
                        <p style={{ color: '#888780' }}>Cargando…</p>
                    ) : status.length === 0 ? (
                        <p style={{ color: '#888780' }}>Todavía no hay clientes conectados a Mi ANSES.</p>
                    ) : (
                        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                            <table className="table table-sm table-hover">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Estado</th>
                                        <th>Última sync</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {status.map((c) => (
                                        <tr key={c.id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{c.clienteNombre || c.cuil}</div>
                                                <div style={{ fontSize: 11, color: '#888780' }}>{c.cuil}</div>
                                            </td>
                                            <td>
                                                <span className={`anses-status-badge ${c.estado}`}>{c.estado}</span>
                                            </td>
                                            <td style={{ fontSize: 13 }}>{formatearFecha(c.ultima_sync)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="anses-admin-card anses-admin-card-full">
                    <AvisosPorUsuario />
                </div>

                <div className="anses-admin-card anses-admin-card-full">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <h6 style={{ margin: 0 }}>Logs de sincronización</h6>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 200 }}
                            value={filtroEstadoLog}
                            onChange={(e) => setFiltroEstadoLog(e.target.value)}
                        >
                            <option value="TODOS">Todos los estados</option>
                            <option value="OK">OK</option>
                            <option value="ERROR">ERROR</option>
                            <option value="CHALLENGE">CHALLENGE</option>
                            <option value="SESION_EXPIRADA">SESION_EXPIRADA</option>
                        </select>
                    </div>
                    {cargandoLogs ? (
                        <p style={{ color: '#888780' }}>Cargando…</p>
                    ) : logsFiltrados.length === 0 ? (
                        <p style={{ color: '#888780' }}>No hay logs para mostrar.</p>
                    ) : (
                        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                            <table className="table table-sm table-hover">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Hora</th>
                                        <th>Usuario</th>
                                        <th>Nombre Cliente</th>
                                        <th>CUIL Cliente</th>
                                        <th>Estado</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logsFiltrados.map((l) => {
                                        const { fecha, hora } = formatearFechaHora(l.inicio_en);
                                        return (
                                            <tr key={l.id}>
                                                <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fecha}</td>
                                                <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{hora}</td>
                                                <td style={{ fontSize: 13 }}>{l.iniciado_por || 'Sistema'}</td>
                                                <td style={{ fontSize: 13, fontWeight: l.clienteNombre ? 500 : 400, color: l.clienteNombre ? '#1e293b' : '#9ca3af' }}>
                                                    {l.clienteNombre || '—'}
                                                </td>
                                                <td style={{ fontSize: 12, color: '#64748b' }}>{l.cuil || '—'}</td>
                                                <td>
                                                    <span className={`anses-status-badge ${l.estado}`}>{l.estado}</span>
                                                </td>
                                                <td style={{ fontSize: 12, color: '#993556', maxWidth: 280 }}>
                                                    {l.mensaje_error || ''}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminAnsesSearchPage;
